import React, { useEffect, useState } from "react";
import CustomSearchFieldTextInput from "../../../../components/custom/form/CustomSearchFieldTextInput";
import { Button } from "@progress/kendo-react-buttons";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridNoRecords,
  GridSortChangeEvent,
} from "@progress/kendo-react-grid";
import { SortDescriptor, orderBy, process } from "@progress/kendo-data-query";
import { Tooltip } from "@progress/kendo-react-tooltip";
import UpsertAPIAccess from "./UpsertAPIAccess";
import { APIToken } from "./APIAccessProps";
import moment from "moment";
import apiService from "../../../../services/apiAccess.service";
import useAuth from "../../../../hooks/useAuth";
import { Loader } from "@progress/kendo-react-indicators";
import { TinyUserGroup } from "../../../../types/user/UserGroupRelation";
import { formatDateTime } from "../../../../utils/dateTimeUtils";
import { SweetAlertOptions } from "sweetalert2";
import { AxiosError } from "axios";
import useSwal from "../../../../hooks/useSwal";
import { User } from "../../../../types/user";
import usersService from "../../../../services/users.service";
import { Dictionary } from "../../../../types/Dictionary";
import useLocale from "../../../../hooks/useLocale";
import useMasterData from "../../../../hooks/useMasterData";
import APIAccessForMobile from "./APIAccessForMobile";
import useTranslation from "../../../../hooks/useTranslation";

interface Page {
  skip: number;
  take: number;
}

interface APIAccessProps {
  setPageFreezeLoader: React.Dispatch<React.SetStateAction<boolean>>;
}

const APIAccess: React.FC<APIAccessProps> = ({ setPageFreezeLoader }) => {
  const initialDataState: Page = {
    skip: 0,
    take: 10,
  };
  const trans = useTranslation("APIAccess");
  const localeCtx = useLocale();
  const auth = useAuth();
  const swal = useSwal();
  const masterData = useMasterData();
  const [visible, setVisible] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [pageData, setPageData] = useState<Page>(initialDataState);
  const [tokens, setTokens] = useState<APIToken[]>([]);
  const [upsertTokenData, setUpsertTokenData] = useState<APIToken | undefined>();
  const [sort, setSort] = useState([
    { field: "tokenExpiry", dir: "asc" },
  ] as SortDescriptor[]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<User>();

  useEffect(() => {
    if (
      !localeCtx?.selectedLocale?.current.componentTranslations[
      "APIAccess"
      ]
    ) {
      trans.fetchTranslations("APIAccess");
    }
  }, [localeCtx?.selectedLocale]);

  useEffect(() => {
    fetchTokens();
  }, [auth?.tokenPayload?.CustomerId]);

  useEffect(() => {
    getCurrentUser();
  }, [])

  const fetchTokens = async () => {
    setLoading(true);
    setError(false);
    try{
      const allTokens:APIToken[]=await apiService.getAllTokens(auth?.tokenPayload?.CustomerId??0);
      const sortedTokens: APIToken[] = allTokens.sort((a: APIToken, b: APIToken) => {
        if (moment(a.tokenExpiry) > moment(b.tokenExpiry))
          return 1;
        if (moment(a.tokenExpiry) < moment(b.tokenExpiry))
          return -1;
        return 0;
      });
      setTokens(sortedTokens);
    } catch (error) {
      setError(true);
      setTokens([])
    } finally {
      setLoading(false);
    }
  };

  const getCurrentUser = async () => {
    try {
      const user = await usersService.getCurrentUser();
      setCurrentUser(user);
    } catch (ex) {
      if (ex instanceof Error) {
        console.log(ex);
      }
    }
  };

  const toggleDialog = () => {
    setVisible(!visible);
    setUpsertTokenData(undefined);
  };

  const searchToken = (tokenData: APIToken) => {
    const searchVal = searchTerm.trim().toLocaleLowerCase();
    if (tokenData.tokenName.toLocaleLowerCase().includes(searchVal)) {
      return true;
    }
    return false;
  };

  const handleSearchChange = (e: React.FormEvent<HTMLInputElement>) => {
    setSearchTerm(e.currentTarget.value);
    setPageData(initialDataState);
  };

  const handlePageChange = (event: any) => {
    setPageData(event.page);
  };

  const apiAccessUpsertHandler = (updatedToken?: APIToken, insertedToken?: APIToken) => {
    if (insertedToken) {
      const updatedTokensList: APIToken[] = [...tokens, insertedToken]
      setSearchTerm("");
      setTokens(updatedTokensList);
      const currentPageNumber = Math.floor(
        updatedTokensList.length / pageData.take
      );
      setPageData({
        skip:
          updatedTokensList.length % pageData.take === 0
            ? (currentPageNumber - 1) * pageData.take
            : currentPageNumber * pageData.take,
        take: pageData.take,
      });
    } else {
      if (updatedToken) {
        const updatedTokensList: APIToken[] =
          tokens.map((token: APIToken) => {
            if (token.id === updatedToken.id) {
              token.tokenExpiry =
                updatedToken.tokenExpiry;
            }
            return token;
          });
        setTokens(updatedTokensList);
      }
    }
  }

  const StatusCustomCell = (props: GridCellProps) => {
    return (
      <td className="uCardCol mx-th-tag">
        <div className="tblUsr">
          <div className="topUsrAreaPic">
            <div className="topUsrAreaPic-tx">
              <span className="cursor-default text-decoration-none float-left w-100 line-height-1 mx-td-spn">
                <i style={{ fontSize: "8px", color: new Date() > new Date(props.dataItem.tokenExpiry) ? "red" : "blue" }}
                  className="bi bi-circle-fill"></i>&nbsp;{new Date() > new Date(props.dataItem.tokenExpiry) ?
                    trans.fetchLabelKeyTranslation("ExpiredStatusText", " Expired")
                    : trans.fetchLabelKeyTranslation("ActiveStatusText", " Active")}
              </span>
            </div>
          </div>
        </div>
      </td>
    );
  };

  const AuthorizationProfileCustomCell = (props: GridCellProps) => {
    return (
      <td className="uCardCol mx-th-tag">
        <div className="tblUsr">
          <div className="topUsrAreaPic">
            <div className="topUsrAreaPic-tx">
              <span className="cursor-default text-decoration-none float-left w-100 line-height-1 mx-td-spn">
                <span className="badgeList">
                  <div className="keywordRow">
                    {props.dataItem.authorizationProfile.name}
                  </div>
                </span>
              </span>
            </div>
          </div>
        </div>
      </td>
    );
  };

  const UserGroupsCustomCell = (props: GridCellProps) => {
    return (
      <td className="uCardCol mx-th-tag">
        <div className="tblUsr">
          <div className="topUsrAreaPic">
            <div className="topUsrAreaPic-tx">
              <span className="cursor-default text-decoration-none float-left w-100 line-height-1 mx-td-spn">
                {props.dataItem.recordingUserGroups.map((group: TinyUserGroup) => {
                  return (
                    <div className="keywordTrk">{group.name}</div>
                  )
                })}
              </span>
            </div>
          </div>
        </div>
      </td>
    );
  };

  const TokenExpiryCustomCell = (props: GridCellProps) => {
    return (
      <td className="uCardCol mx-th-tag">
        <div className="tblUsr">
          <div className="topUsrAreaPic">
            <div className="topUsrAreaPic-tx">
              <span className="cursor-default text-decoration-none float-left w-100 line-height-1 mx-td-spn">
                {formatDateTime(props.dataItem.tokenExpiry, "MMM DD, YYYY, HH:mm")}
              </span>
            </div>
          </div>
        </div>
      </td>
    );
  };
  const onEditHandler = (data: APIToken) => {
    setUpsertTokenData(data);
    setVisible(true);
  };

  const onDeleteHandler = async (data: APIToken) => {
    setPageFreezeLoader(false);
    const swalOptions: SweetAlertOptions = {
      title: trans.fetchLabelKeyTranslation("SwtWarningDltTitle", "Are you sure?"),
      text: trans.fetchLabelKeyTranslation("SwtWarningDltText", "You won't be able to revert this!"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: trans.fetchLabelKeyTranslation("SwtWarningDltConfirmText", "Confirm"),
      cancelButtonText: trans.fetchLabelKeyTranslation("SwtWarningDltCancelText", "Cancel"),
    };
    const result = await swal.fire(swalOptions);
    if (result.isConfirmed) {
      setPageFreezeLoader(true);
      try {
        await apiService.deleteToken(data.id, auth?.tokenPayload?.CustomerId ?? 0);
        setPageFreezeLoader(false);
        const swalOptions: SweetAlertOptions = {
          icon: "success",
          title: trans.fetchLabelKeyTranslation("SwtSuccessDltTitle", "Token is deleted"),
          confirmButtonText: trans.fetchLabelKeyTranslation("SwtSuccessDltConfirmText", "OK"),
        };
        await swal.fire(swalOptions);
        const updatedTokens = tokens?.filter(
          (token: APIToken) => token.id !== data.id
        );
        setTokens(updatedTokens);
        const gridDataResult = process(
          updatedTokens.filter((token: APIToken) =>
            searchToken(token)
          ),
          pageData
        );
        if (
          gridDataResult.data.length === 0 &&
          gridDataResult.total > 0
        ) {
          const currentPageNumber = Math.floor(
            gridDataResult.total / pageData.take
          );
          setPageData({
            skip:
              gridDataResult.total % pageData.take === 0
                ? (currentPageNumber - 1) * pageData.take
                : currentPageNumber * pageData.take,
            take: pageData.take,
          });
        }
      }
      catch (err) {
        if (err instanceof AxiosError) {
          let errorMessage: string = trans.fetchLabelKeyTranslation("SwtFailureDltErrorMssg", "Uh Oh! Something Went Wrong. Please Try Again!");
          const swalOptions: SweetAlertOptions = {
            icon: "error",
            title: trans.fetchLabelKeyTranslation("SwtFailureDltTitle", "Error"),
            text: errorMessage,
            confirmButtonText: trans.fetchLabelKeyTranslation("SwtFailureDltConfirmText", "OK"),
          };
          swal.fire(swalOptions);
        }
      } finally {
        setPageFreezeLoader(false);
      }
    }
  };

  const CustomCellWithActions = (props: GridCellProps) => {
    return (
      <td className="text-right">
        <Tooltip anchorElement={"target"} position={"top"} parentTitle={true}>
          <span className="iBtn d-flex justify-content">
            <Button
              className={`radius-50 iBtn-bg d-flex align-items-center justify-conter m-r-3 iconBtn 
              ${masterData?.telepoDisableConfig?.systemSetting.apiAccess.disableChanges ? "disabledBtn" : ""
                }`}
              onClick={() => onEditHandler(props.dataItem)}
              disabled={
                masterData?.telepoDisableConfig?.systemSetting.apiAccess
                  .disableChanges
              }
            >
              <span className="fs-17 line-height-1 text-primary" title={trans.fetchLabelKeyTranslation("EditTitleText", "Edit")}>
                <i className="bi bi-pencil"></i>
              </span>
            </Button>
            <Button
              className={`radius-50 iBtn-bg d-flex align-items-center justify-conter m-r-3 iconBtn
              ${masterData?.telepoDisableConfig?.systemSetting.apiAccess.disableChanges ? "disabledBtn" : ""
                }`}
              onClick={() => onDeleteHandler(props.dataItem)}
              disabled={
                masterData?.telepoDisableConfig?.systemSetting.apiAccess
                  .disableChanges
              }
            >
              <span
                className="fs-17 line-height-1 text-primary"
                title={trans.fetchLabelKeyTranslation("DeleteTitleText", "Delete")}
              >
                <i className="bi bi-trash3"></i>
              </span>
            </Button>
          </span>
        </Tooltip>
      </td>
    );
  };

  const CustomCellForMobileView = (mobileViewProps: GridCellProps) => {
    return (
      <APIAccessForMobile
        {...mobileViewProps}
        loading={loading}
        error={error}
        onEditHandler={onEditHandler}
        onDeleteHandler={onDeleteHandler}
      />
    );
  };

  return (
    <div className="callList float-left w-100 maxTableCol m-b-20">
      <div className="callListInner">
        <div className="callListBox">
          <div className="row">
            <div className="col-md-12">
              <div className="tableTitle d-flex flex-wrap align-items-center justify-content-between trk-t text-black-14 border-bottom-solid border-w-1 border-black-1">
                <span className="text-primary">{trans.fetchLabelKeyTranslation("APIAccessTitle", "API Access")}</span>
                <div className="buttons-container hov-transparent d-flex flex-wrap align-itmes-center">
                  <div className="searchBox searchCol m-r-10 p-b-3">
                    <CustomSearchFieldTextInput
                      className="input-search"
                      placeholder={trans.fetchLabelKeyTranslation("SearchPlaceholderText", "Search...")}
                      value={searchTerm}
                      updateValue={setSearchTerm}
                      searchTextChangeHandler={handleSearchChange}
                      onEscapeKeyFunc={true}
                      disabled={loading}
                    />
                  </div>
                  <Button
                    disabled={loading ||
                      masterData?.telepoDisableConfig?.systemSetting.apiAccess.disableChanges}
                    onClick={toggleDialog}
                    className={`btn bg-primary text-white fs-13 fw-normal
                      ${loading || masterData?.telepoDisableConfig?.systemSetting.apiAccess.disableChanges ? "disabledBtn" : ""}`}
                    style={{ height: "29px", margin: "-1px 0 0 0" }}
                    title={`+ ${trans.fetchLabelKeyTranslation("NewTokenTitle", "New Token")}`}
                  >
                    {`+ ${trans.fetchLabelKeyTranslation("NewTokenTitle", "New Token")}`}
                  </Button>
                  {visible && (
                    <UpsertAPIAccess
                      toggleDialog={toggleDialog}
                      upsertTokenData={upsertTokenData}
                      apiAccessUpsertHandler={apiAccessUpsertHandler}
                      currentUser={currentUser}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="tableList rowSpace float-left w-100">
                <div className="tableMain gridContentOverflowY-hidden table-mobile">
                  <Grid
                    data={orderBy(
                      tokens
                        .filter((token: APIToken) => searchToken(token))
                        .slice(
                          pageData.skip,
                          pageData.take + pageData.skip
                        ) ?? [],
                      sort
                    )}
                    skip={pageData.skip}
                    take={pageData.take}
                    total={
                      tokens?.filter((token: APIToken) => searchToken(token))
                        .length
                    }
                    pageable={{ buttonCount: 4, pageSizes: true }}
                    onPageChange={handlePageChange}
                    sort={sort}
                    sortable={true}
                    onSortChange={(e: GridSortChangeEvent) => {
                      let tempSort = e.sort;
                      if (e.sort.length === 0 && sort.length !== 0) {
                        tempSort = sort.map((s) => {
                          return { field: s.field, dir: "asc" };
                        });
                      }
                      setTokens(
                        orderBy(
                          tokens?.filter((token: APIToken) =>
                            searchToken(token)
                          ) ?? [],
                          tempSort
                        )
                      );
                      setSort(tempSort);
                    }}
                  >
                    <GridNoRecords>
                      {!loading && error ? <span className="fs-15">
                        <i className="bi bi-exclamation-triangle-fill tx-amber"></i>
                        {trans.fetchLabelKeyTranslation("FetchTokensErrMssg", "Uh Oh! Something Went Wrong. Please Try Again!")}
                      </span> : loading ? <Loader type={"infinite-spinner"} /> : trans.fetchLabelKeyTranslation("NoTokensFoundErrMssg", "No tokens found")}
                    </GridNoRecords>
                    <GridColumn
                      width={0}
                      field="cellMobile"
                      cell={CustomCellForMobileView}
                    />
                    <GridColumn
                      field="tokenName"
                      title={trans.fetchLabelKeyTranslation("TokenNameGridTitle", "Token name")}
                      sortable={false}
                    />
                    <GridColumn
                      title={trans.fetchLabelKeyTranslation("StatusGridTitle", "Status")}
                      cell={StatusCustomCell}
                      sortable={false}
                    />
                    <GridColumn
                      field="authorizationProfile"
                      title={trans.fetchLabelKeyTranslation("PermissionGroupGridTitle", "Permission Group")}
                      cell={AuthorizationProfileCustomCell}
                      sortable={false}
                    />
                    <GridColumn
                      field="recordingNetworkAccessibility.name"
                      title={trans.fetchLabelKeyTranslation("RecordingNetworkGridTitle", "Recording Network")}
                      sortable={false}
                    />
                    <GridColumn
                      field="recordingUserGroups"
                      title={trans.fetchLabelKeyTranslation("UserGroupsGridTitle", "User Groups")}
                      cell={UserGroupsCustomCell}
                      sortable={false}
                    />
                    <GridColumn
                      field="tokenExpiry"
                      title={trans.fetchLabelKeyTranslation("TokenExpiryGridTitle", "Token Expiry")}
                      cell={TokenExpiryCustomCell}
                      sortable={true}
                    />
                    <GridColumn
                      field="apiAccessActions"
                      title={trans.fetchLabelKeyTranslation("ActionsGridTitle", "Actions")}
                      cell={CustomCellWithActions}
                      sortable={false}
                      headerClassName="text-center"
                    />
                  </Grid>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APIAccess;
